import React, { useEffect } from 'react';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { customStyles } from '../styles';
import { useNJTaxSearch } from '../NJTaxSearchContext';
import TextField from 'components/ui/TextField';
import TextArea from 'components/forms/ui/TextArea';
import CurrencyField from 'components/ui/CurrencyField';
import { PropertyInformation, WaterInfo } from '../types';
import ConvertToTitleCaseElement from 'components/forms/ConvertTitleCaseElement';
import { FormContainer, FormRow } from './formstyles';

interface Props {
  index: number;
  water: WaterInfo;
  changeWater: any;
  propertyInfo?: PropertyInformation;
}

const selectFullWidthStyles = {
  ...customStyles,
  container: (provided: any) => ({
    ...provided,
    width: '100%',
  }),
};

const WaterSewerInfo = (props: Props) => {
  const propertyInfo = props.propertyInfo || useNJTaxSearch().propertyInfo;
  const typeOptions = ['Water', 'Sewer', 'Electric', 'CCMUA', 'Trash', 'Solid Waste', 'Other'];
  const descriptionOptions = [
    'Public',
    'Private',
    'Not Available',
    'Vacant Land',
    'Included in water account',
    'Included with Taxes',
    'Common Meter',
  ];
  const statusOptions = [
    'Paid',
    'Paid in Full',
    'Open',
    'Open + Penalty',
    'Included in Lien Below',
    'Included with Taxes',
    'Billed',
    'To be Determined',
    'Other',
  ];

  useEffect(() => {
    if (propertyInfo?.assessorCode?.label?.includes('Vacant Land')) {
      const addedInfo = props.water?.additionalInfo ? props.water?.additionalInfo : '';
      if (!addedInfo.includes('Unimproved, if premise is improved utility charges a lien')) {
        props.changeWater(
          props.index,
          'additionalInfo',
          'Unimproved, if premise is improved utility charges a lien\n' + addedInfo
        );
      }
    }
  }, [propertyInfo?.assessorCode?.label]);
  return (
    <FormContainer>
      {props.water.finalReading && <div className="finalReading">This account had a final reading</div>}
      {props.water.dateRange && <div className="dateRange">Period: {props.water.dateRange} </div>}
      <FormRow style={{ display: 'flex' }}>
        <div>
          <span className="fieldlabel">Type</span>
          <Select
            width="100%"
            styles={selectFullWidthStyles}
            components={{
              IndicatorSeparator: null,
            }}
            options={typeOptions.map(value => ({ label: value, value }))}
            name="type"
            value={props.water.type || { value: null, label: '' }}
            onChange={e => {
              props.changeWater(props.index, 'type', e);
            }}
          />
        </div>
        <div>
          <span className="fieldlabel">Description</span>
          <Select
            isClearable
            width="100%"
            styles={selectFullWidthStyles}
            components={{
              IndicatorSeparator: null,
            }}
            options={descriptionOptions.map(value => ({ label: value, value }))}
            name="description"
            value={props.water.description || { value: null, label: '' }}
            onChange={e => {
              props.changeWater(props.index, 'description', e);
            }}
          />
        </div>
      </FormRow>
      <FormRow>
        <CurrencyField
          label="Amount"
          width="100%"
          value={props.water.amount}
          onChange={e => {
            props.changeWater(props.index, 'amount', e);
          }}
        />
        <div>
          <span className="fieldlabel">Status</span>
          <CreatableSelect
            width="100%"
            styles={selectFullWidthStyles}
            components={{
              IndicatorSeparator: null,
            }}
            options={statusOptions.map(value => ({ label: value, value }))}
            onChange={e => {
              props.changeWater(props.index, 'status', e);
            }}
            value={props.water.status || { value: null, label: '' }}
          />
        </div>
      </FormRow>
      <FormRow>
        <TextField
          label="Account Number"
          width="100%"
          value={props.water.accountNumber || ''}
          onChange={e => {
            props.changeWater(props.index, 'accountNumber', e.target.value);
          }}
        />
        <TextField
          label="Date Range"
          width="100%"
          value={props.water.readDateRange || props.water.dateRange || ''}
          onChange={e => {
            props.changeWater(props.index, 'readDateRange', e.target.value);
          }}
        />
      </FormRow>
      <FormRow>
        <CurrencyField
          label="Delinquent Amount"
          width="100%"
          value={props.water.delinquentAmount}
          onChange={e => {
            props.changeWater(props.index, 'delinquentAmount', e);
          }}
        />
        <div>
          <span className="fieldlabel">Delinquent Status:</span>
          <CreatableSelect
            width="100%"
            styles={selectFullWidthStyles}
            components={{
              IndicatorSeparator: null,
            }}
            options={statusOptions.map(value => ({ label: value, value }))}
            onChange={e => {
              props.changeWater(props.index, 'delinquentStatus', e);
            }}
            value={props.water.delinquentStatus || { value: null, label: '' }}
          />
        </div>
      </FormRow>
      <FormRow>
        <ConvertToTitleCaseElement
          onIconClick={newStr => {
            props.changeWater(props.index, 'additionalInfo', newStr);
          }}
          originalString={props.water.additionalInfo || ''}
        >
          <TextArea
            value={props.water.additionalInfo || ''}
            onChange={(e: any) => props.changeWater(props.index, 'additionalInfo', e.target.value)}
            styles={{
              marginTop: 8,
              width: '100%',
              height: 48,
              borderRadius: 5,
              resize: 'vertical',
              ':focus': {
                outline: 'none',
              },
            }}
            label="Additional Water Information:"
            error={false}
            placeholder=""
            maxLength={52000}
          />
        </ConvertToTitleCaseElement>
      </FormRow>
    </FormContainer>
  );
};

export default WaterSewerInfo;
